import { z } from 'zod';

/**
 * The "second life" economy: jobs that pay Kaki Dollars, player shops and the
 * marketplace, and transfers between players.
 *
 * Live work shifts travel over the WebSocket (every type starts with `job:`
 * or `shop:` so the gateway routes it here); buying, listing and transfers
 * are HTTP. The server generates and marks every task -- the client only
 * displays them.
 */

/* -------------------------------------------------------------------------- */
/* Careers and balancing                                                      */
/* -------------------------------------------------------------------------- */

export const JOB_IDS = ['barista', 'clerk', 'cashier', 'chef'] as const;
export type JobId = (typeof JOB_IDS)[number];

export interface JobDef {
  id: JobId;
  label: string;
  /** Furniture kind a worker clocks in at. */
  station: string;
  /** Where the public stations are. */
  workplace: string;
  /** K$ per correct task at level 1. */
  baseWage: number;
  /** Titles for levels 1..MAX_LEVEL. */
  titles: readonly string[];
}

export const JOBS: Readonly<Record<JobId, JobDef>> = Object.freeze({
  barista: {
    id: 'barista',
    label: 'Kopi Barista',
    station: 'barista_station',
    workplace: 'Kopi Kaki Kopitiam or the Hawker Centre',
    baseWage: 12,
    // Levels 1-5 are exactly the titles live today -- anyone already at one of
    // them keeps the same title and pay. 6-10 are new, higher tiers above.
    titles: [
      'Trainee',
      'Barista',
      'Senior Barista',
      'Head Barista',
      'Cafe Manager',
      'Regional Manager',
      'Roastery Lead',
      'Master Roaster',
      'Kopi Connoisseur',
      'Kopi Legend',
    ],
  },
  clerk: {
    id: 'clerk',
    label: 'Office Clerk',
    station: 'office_desk',
    workplace: 'Raffles Place Office Tower',
    baseWage: 14,
    titles: [
      'Intern',
      'Clerk',
      'Executive',
      'Manager',
      'Director',
      'Senior Director',
      'Assistant Vice President',
      'Vice President',
      'Managing Director',
      'Executive Director',
    ],
  },
  cashier: {
    id: 'cashier',
    label: 'Mall Cashier',
    station: 'cash_register',
    workplace: 'Orchard Kaki Mall',
    baseWage: 12,
    titles: [
      'Trainee',
      'Cashier',
      'Senior Cashier',
      'Supervisor',
      'Store Manager',
      'Area Manager',
      'Regional Manager',
      'District Manager',
      'Retail Director',
      'Retail Legend',
    ],
  },
  chef: {
    id: 'chef',
    label: 'Hawker Chef',
    station: 'hawker_stall',
    workplace: 'Kopi Kaki Hawker Centre',
    baseWage: 15,
    titles: [
      'Kitchen Helper',
      'Cook',
      'Stall Chef',
      'Head Chef',
      'Hawker Legend',
      'Master Chef',
      'Signature Chef',
      'Celebrity Chef',
      'Hawker Icon',
      'National Treasure',
    ],
  },
});

export function jobForStation(kind: string): JobId | undefined {
  return JOB_IDS.find((id) => JOBS[id].station === kind);
}

/** Tasks per shift. */
export const SHIFT_TASKS = 8;
/** Shifts a registered player may start per UTC day, across all careers. */
export const MAX_SHIFTS_PER_DAY = 12;
/** Guests may work a little, at reduced pay, to get a taste of it. */
export const GUEST_MAX_SHIFTS_PER_DAY = 3;
export const GUEST_PAY_RATE = 0.5;
/** Answers faster than this are treated as automated and marked wrong. */
export const MIN_ANSWER_MS = 300;
/** Job XP gained per correct task. */
export const XP_PER_TASK = 5;
/** XP needed to reach each level (index 0 = level 1). */
// Levels 1-5 keep the exact thresholds and multipliers live today, so no
// current player's level or pay rate changes; 6-10 are new, higher tiers
// that continue the same growth curve above the old cap.
export const LEVEL_XP = [0, 60, 180, 400, 800, 1300, 2000, 3000, 4400, 6400] as const;
/** Pay multiplier per level. */
export const LEVEL_PAY = [1, 1.25, 1.5, 1.8, 2.2, 2.6, 3.1, 3.7, 4.4, 5.2] as const;
export const MAX_LEVEL = LEVEL_XP.length;

export function levelForXp(xp: number): number {
  let level = 1;
  for (let i = 0; i < LEVEL_XP.length; i += 1) if (xp >= LEVEL_XP[i]!) level = i + 1;
  return level;
}

/** XP at which the next level starts, or null at the top level. */
export function nextLevelXp(level: number): number | null {
  return level >= MAX_LEVEL ? null : LEVEL_XP[level]!;
}

/** Streak bonus: +K$2 for each consecutive correct answer beyond the first, capped at +K$10. */
export function streakBonus(streak: number): number {
  return Math.min(Math.max(streak - 1, 0), 5) * 2;
}

/** Pay for one correct task. */
export function taskPay(job: JobId, level: number, streak: number, guest: boolean): number {
  const lvl = Math.min(Math.max(level, 1), MAX_LEVEL);
  const base = Math.round(JOBS[job].baseWage * LEVEL_PAY[lvl - 1]!) + streakBonus(streak);
  return Math.max(1, Math.floor(base * (guest ? GUEST_PAY_RATE : 1)));
}

/* -------------------------------------------------------------------------- */
/* Shops, marketplace, transfers                                              */
/* -------------------------------------------------------------------------- */

export const SHOP_OPEN_FEE = 200;
export const MAX_ACTIVE_LISTINGS = 20;
export const MIN_LISTING_PRICE = 1;
export const MAX_LISTING_PRICE = 1_000_000;
/** Marketplace fee, a money sink taken from the seller's proceeds. */
export const MARKET_FEE_PERCENT = 5;
export const MAX_SHOP_NAME_LENGTH = 32;
export const MAX_SHOP_DESCRIPTION_LENGTH = 140;
export const TRANSFER_DAILY_CAP = 5_000;
export const MAX_TRANSFER_NOTE_LENGTH = 80;

export function marketFee(price: number): number {
  return Math.floor((price * MARKET_FEE_PERCENT) / 100);
}

/* -------------------------------------------------------------------------- */
/* Task content                                                               */
/* -------------------------------------------------------------------------- */

export const COFFEE_BASES = ['kopi', 'teh', 'milo', 'yuanyang'] as const;
export const MILKS = ['condensed', 'evaporated', 'none'] as const;
export const SUGARS = ['kosong', 'siew_dai', 'normal', 'ga_dai'] as const;
export const STRENGTHS = ['normal', 'gao', 'po', 'dinosaur'] as const;

export const baristaAnswerSchema = z.object({
  kind: z.literal('barista'),
  base: z.enum(COFFEE_BASES),
  milk: z.enum(MILKS),
  sugar: z.enum(SUGARS),
  strength: z.enum(STRENGTHS),
  iced: z.boolean(),
});
export type BaristaAnswer = z.infer<typeof baristaAnswerSchema>;

/** Kopitiam lingo, for the in-game glossary. */
export const KOPI_GLOSSARY: ReadonlyArray<[term: string, meaning: string]> = [
  ['Kopi', 'Coffee with condensed milk (already sweet)'],
  ['Teh', 'Tea with condensed milk'],
  ['Milo', 'Chocolate malt drink'],
  ['Yuan Yang', 'Coffee and tea mixed'],
  ['O', 'No milk, sugar only'],
  ['C', 'Evaporated milk and sugar'],
  ['Kosong', 'No sugar'],
  ['Siew dai', 'Less sweet'],
  ['Ga dai', 'Extra sweet'],
  ['Gao', 'Extra strong'],
  ['Po', 'Weak'],
  ['Peng', 'Iced'],
  ['Dinosaur', 'Milo with a mountain of Milo powder on top'],
];

export const DEPARTMENTS = ['Finance', 'HR', 'Sales', 'IT', 'Legal'] as const;
export type Department = (typeof DEPARTMENTS)[number];

export const CASH_DENOMINATIONS_CENTS = [5000, 1000, 500, 200, 100, 50, 20, 10, 5] as const;

export const jobTaskSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('barista'), customer: z.string(), order: z.string() }),
  z.object({ kind: z.literal('clerk_sort'), document: z.string(), trays: z.array(z.string()) }),
  z.object({
    kind: z.literal('clerk_invoice'),
    lines: z.array(z.object({ item: z.string(), qty: z.number().int(), unitCents: z.number().int() })),
    claimedTotalCents: z.number().int(),
  }),
  z.object({ kind: z.literal('clerk_type'), phrase: z.string() }),
  z.object({ kind: z.literal('cashier'), totalCents: z.number().int(), paidCents: z.number().int() }),
  z.object({ kind: z.literal('chef'), dish: z.string(), options: z.array(z.string()), steps: z.number().int() }),
]);
export type JobTask = z.infer<typeof jobTaskSchema>;

export const jobAnswerSchema = z.discriminatedUnion('kind', [
  baristaAnswerSchema,
  z.object({ kind: z.literal('clerk_sort'), tray: z.string().max(32) }),
  z.object({ kind: z.literal('clerk_invoice'), correct: z.boolean() }),
  z.object({ kind: z.literal('clerk_type'), text: z.string().max(200) }),
  z.object({ kind: z.literal('cashier'), changeCents: z.number().int().min(0).max(1_000_000) }),
  z.object({ kind: z.literal('chef'), order: z.array(z.string().max(40)).max(12) }),
]);
export type JobAnswer = z.infer<typeof jobAnswerSchema>;

const jobIdSchema = z.enum(JOB_IDS);

/* -------------------------------------------------------------------------- */
/* Messages                                                                   */
/* -------------------------------------------------------------------------- */

export const economyClientMessageSchema = z.discriminatedUnion('t', [
  /** Clock in at the job station the player is standing next to. */
  z.object({ t: z.literal('job:start'), itemId: z.string().min(1).max(64) }),
  z.object({ t: z.literal('job:answer'), answer: jobAnswerSchema }),
  /** Clock out early; completed tasks are still paid. */
  z.object({ t: z.literal('job:quit') }),
]);

export const economyServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('job:started'),
    job: jobIdSchema,
    itemId: z.string(),
    level: z.number().int(),
    title: z.string(),
    totalTasks: z.number().int(),
  }),
  z.object({
    t: z.literal('job:task'),
    index: z.number().int(),
    total: z.number().int(),
    task: jobTaskSchema,
    /** Server clock time the task expires. */
    deadline: z.number(),
    timeLimitMs: z.number().int(),
  }),
  z.object({
    t: z.literal('job:result'),
    correct: z.boolean(),
    /** Why it was wrong (or the praise), shown on the task card. */
    feedback: z.string(),
    pay: z.number().int(),
    streak: z.number().int(),
    earned: z.number().int(),
  }),
  z.object({
    t: z.literal('job:ended'),
    reason: z.string(),
    earned: z.number().int(),
    correct: z.number().int(),
    total: z.number().int(),
    level: z.number().int(),
    title: z.string(),
    promoted: z.boolean(),
  }),
  /** Broadcast to the room: someone started (job) or stopped (null) working. */
  z.object({ t: z.literal('job:working'), avatarId: z.string(), job: jobIdSchema.nullable() }),
  /** To a seller: one of their listings sold. */
  z.object({
    t: z.literal('shop:sold'),
    listingId: z.string(),
    kind: z.string(),
    price: z.number().int(),
    net: z.number().int(),
    buyerName: z.string(),
  }),
]);

export type EconomyClientMessage = z.infer<typeof economyClientMessageSchema>;
export type EconomyServerMessage = z.infer<typeof economyServerMessageSchema>;

/* -------------------------------------------------------------------------- */
/* HTTP shapes                                                                */
/* -------------------------------------------------------------------------- */

export interface JobProgressView {
  id: JobId;
  label: string;
  station: string;
  workplace: string;
  level: number;
  title: string;
  xp: number;
  nextLevelXp: number | null;
  totalEarned: number;
  tasksDone: number;
  /** K$ per correct task at the current level, before streak bonus. */
  wage: number;
}

export interface JobsOverview {
  jobs: JobProgressView[];
  shiftsToday: number;
  maxShifts: number;
  guest: boolean;
}

export interface ShopSummary {
  id: string;
  ownerId: string;
  ownerName: string;
  name: string;
  description: string;
  roomId: string | null;
  roomName: string | null;
  activeListings: number;
  totalSales: number;
  createdAt: number;
}

export type ListingCategory = 'furniture' | 'wardrobe' | 'designs' | 'other';

export interface ListingView {
  id: string;
  shopId: string;
  shopName: string;
  sellerId: string;
  sellerName: string;
  kind: string;
  label: string;
  category: ListingCategory;
  price: number;
  status: 'active' | 'sold' | 'cancelled';
  createdAt: number;
  soldAt: number | null;
  buyerName: string | null;
}

export type ListingSort = 'newest' | 'price_asc' | 'price_desc';
